import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar';
import { formatCurrency, formatDate, getResponsiveThumbnailUrl } from '@/lib/utils';
import { Search, Gavel, Clock, TrendingUp, User, History, Plus, Users, ChevronDown, ChevronUp } from 'lucide-react';
import { listingsAPI } from '@/lib/api';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';

const DashboardAuctionsTab = () => {
    const { user } = useAuth();
    const navigate = useNavigate();
    const [activeTab, setActiveTab] = useState('my-auctions');
    const [myAuctions, setMyAuctions] = useState([]);
    const [participatingAuctions, setParticipatingAuctions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchQuery, setSearchQuery] = useState('');
    const [expandedAuction, setExpandedAuction] = useState(null);

    useEffect(() => {
        const fetchData = async () => {
            setLoading(true);
            try {
                // Fetch my auctions (created by me) with bidder details
                const myAuctionsRes = await listingsAPI.getMyAuctions();
                setMyAuctions(myAuctionsRes.data || []);
                
                // Fetch auctions I'm participating in (where I bid)
                const participatingRes = await listingsAPI.getMyBids();
                setParticipatingAuctions(participatingRes.data || []);
            } catch (error) {
                console.error('Error fetching auctions:', error);
            } finally {
                setLoading(false);
            }
        };
        fetchData();
    }, [user?.user_id]);

    const getTimeRemaining = (endDate) => {
        if (!endDate) return 'N/A';
        const end = new Date(endDate);
        const now = new Date();
        const diff = end - now;
        
        if (diff <= 0) return 'Încheiată';
        
        const days = Math.floor(diff / (1000 * 60 * 60 * 24));
        const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
        const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
        
        if (days > 0) return `${days}z ${hours}h`;
        if (hours > 0) return `${hours}h ${minutes}m`;
        return `${minutes}m`;
    };

    const getStatusBadge = (auction) => {
        if (auction.status === 'sold') return <Badge className="bg-green-50 text-green-700">Vândută</Badge>;
        if (auction.status === 'expired') return <Badge className="bg-gray-50 text-gray-700">Expirată</Badge>;
        if (auction.status === 'cancelled') return <Badge className="bg-red-50 text-red-700">Anulată</Badge>;
        return <Badge className="bg-blue-50 text-blue-700">Activă</Badge>;
    };

    const filterAuctions = (auctions) => {
        return auctions.filter(a => 
            !searchQuery || 
            a.title?.toLowerCase().includes(searchQuery.toLowerCase()) ||
            a.category_name?.toLowerCase().includes(searchQuery.toLowerCase())
        );
    };

    const toggleExpanded = (auctionId) => {
        setExpandedAuction(expandedAuction === auctionId ? null : auctionId);
    };

    // Card for My Auctions - shows bidder info
    const MyAuctionCard = ({ auction }) => {
        const isExpanded = expandedAuction === auction.listing_id;
        const hasBids = auction.bids && auction.bids.length > 0;
        
        return (
            <Card className="hover:shadow-md transition-all" data-testid={`my-auction-card-${auction.listing_id}`}>
                <CardContent className="p-4">
                    <Link 
                        to={`/listing/${auction.slug || auction.listing_id}`}
                        className="block"
                    >
                        <div className="flex gap-4">
                            {auction.images?.[0] && (
                                <img
                                    src={getResponsiveThumbnailUrl(auction.images[0], 200, 150, 75)}
                                    alt={auction.title}
                                    className="w-20 h-20 object-cover rounded-lg shrink-0"
                                    loading="lazy"
                                />
                            )}
                            <div className="flex-1 min-w-0">
                                <div className="flex items-start justify-between gap-2">
                                    <h3 className="font-medium truncate">{auction.title}</h3>
                                    {getStatusBadge(auction)}
                                </div>
                                <div className="mt-2 flex items-center gap-4 text-sm flex-wrap">
                                    <div className="flex items-center gap-1 text-primary font-semibold">
                                        <TrendingUp className="w-4 h-4" />
                                        {formatCurrency(auction.current_price || auction.starting_price)}
                                    </div>
                                    <div className="flex items-center gap-1 text-muted-foreground">
                                        <Clock className="w-4 h-4" />
                                        {getTimeRemaining(auction.end_date)}
                                    </div>
                                    <div className="flex items-center gap-1 text-muted-foreground">
                                        <Users className="w-4 h-4" />
                                        {auction.bids_count || 0} oferte
                                    </div>
                                </div>
                                {hasBids && auction.highest_bidder_name && (
                                    <p className="text-xs text-muted-foreground mt-1">
                                        Lider: <span className="font-medium text-foreground">{auction.highest_bidder_name}</span> - {formatCurrency(auction.highest_bid)}
                                    </p>
                                )}
                            </div>
                        </div>
                    </Link>
                    
                    {/* Bidders section */}
                    {hasBids && (
                        <div className="mt-3 pt-3 border-t">
                            <Button
                                variant="ghost"
                                size="sm"
                                className="w-full flex items-center justify-between text-muted-foreground hover:text-foreground"
                                onClick={(e) => {
                                    e.preventDefault();
                                    toggleExpanded(auction.listing_id);
                                }}
                                data-testid={`toggle-bids-${auction.listing_id}`}
                            >
                                <span className="flex items-center gap-2">
                                    <Users className="w-4 h-4" />
                                    {auction.unique_bidders_count} licitator{auction.unique_bidders_count !== 1 ? 'i' : ''} • {auction.bids_count} ofert{auction.bids_count !== 1 ? 'e' : 'ă'}
                                </span>
                                {isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                            </Button>
                            
                            {isExpanded && (
                                <div className="mt-3 space-y-2 max-h-48 overflow-y-auto" data-testid={`bids-list-${auction.listing_id}`}>
                                    {auction.bids.map((bid, idx) => (
                                        <div 
                                            key={bid.bid_id} 
                                            className={`flex items-center justify-between p-2 rounded-lg ${idx === 0 ? 'bg-primary/5 border border-primary/20' : 'bg-muted/50'}`}
                                        >
                                            <div className="flex items-center gap-2">
                                                <Avatar className="w-7 h-7">
                                                    {bid.bidder_avatar ? (
                                                        <AvatarImage src={bid.bidder_avatar} alt={bid.bidder_name} />
                                                    ) : null}
                                                    <AvatarFallback className="text-xs">
                                                        {(bid.bidder_name || bid.bidder_username || '?').charAt(0).toUpperCase()}
                                                    </AvatarFallback>
                                                </Avatar>
                                                <div>
                                                    <p className="text-sm font-medium">{bid.bidder_name || bid.bidder_username}</p>
                                                    <p className="text-xs text-muted-foreground">
                                                        {formatDate(bid.created_at)}
                                                    </p>
                                                </div>
                                            </div>
                                            <div className="text-right">
                                                <p className={`font-semibold ${idx === 0 ? 'text-primary' : ''}`}>
                                                    {formatCurrency(bid.amount)}
                                                </p>
                                                {idx === 0 && (
                                                    <Badge variant="outline" className="text-xs bg-primary/10 text-primary border-primary/30">
                                                        Lider
                                                    </Badge>
                                                )}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    )}
                    
                    {!hasBids && (
                        <p className="mt-3 pt-3 border-t text-sm text-muted-foreground text-center">
                            Nicio ofertă încă
                        </p>
                    )}
                </CardContent>
            </Card>
        );
    };

    // Card for Participating Auctions - shows my bid status
    const ParticipatingAuctionCard = ({ auction }) => (
        <Link 
            to={`/listing/${auction.slug || auction.listing_id}`}
            className="block"
        >
            <Card className="hover:shadow-md transition-all hover:border-primary/30 cursor-pointer" data-testid={`participating-auction-card-${auction.listing_id}`}>
                <CardContent className="p-4">
                    <div className="flex gap-4">
                        {auction.images?.[0] && (
                            <img
                                src={getResponsiveThumbnailUrl(auction.images[0], 200, 150, 75)}
                                alt={auction.title}
                                className="w-20 h-20 object-cover rounded-lg shrink-0"
                                loading="lazy"
                            />
                        )}
                        <div className="flex-1 min-w-0">
                            <h3 className="font-medium truncate">{auction.title}</h3>
                            <Badge variant="outline" className="mt-1 text-xs">
                                {auction.category_name}
                            </Badge>
                            <div className="mt-2 flex items-center gap-4 text-sm">
                                <div className="flex items-center gap-1 text-primary font-semibold">
                                    <TrendingUp className="w-4 h-4" />
                                    {formatCurrency(auction.current_price || auction.starting_price)}
                                </div>
                                <div className="flex items-center gap-1 text-muted-foreground">
                                    <Clock className="w-4 h-4" />
                                    {getTimeRemaining(auction.end_date)}
                                </div>
                            </div>
                            {auction.my_highest_bid && (
                                <div className="mt-2 flex items-center gap-2">
                                    <span className="text-xs text-muted-foreground">Oferta ta: {formatCurrency(auction.my_highest_bid)}</span>
                                    {auction.is_winning ? (
                                        <Badge className="bg-green-50 text-green-700 text-xs">Câștigător</Badge>
                                    ) : (
                                        <Badge className="bg-orange-50 text-orange-700 text-xs">Depășit</Badge>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>
                </CardContent>
            </Card>
        </Link>
    );

    if (loading) {
        return (
            <div className="space-y-4">
                <Skeleton className="h-10 w-full" />
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {[...Array(4)].map((_, i) => (
                        <Skeleton key={i} className="h-32" />
                    ))}
                </div>
            </div>
        );
    }

    return (
        <div className="space-y-4" data-testid="dashboard-auctions-tab">
            <Tabs value={activeTab} onValueChange={setActiveTab}>
                <TabsList className="grid w-full grid-cols-2">
                    <TabsTrigger value="my-auctions" className="flex items-center gap-2" data-testid="tab-my-auctions">
                        <Gavel className="w-4 h-4" />
                        <span className="hidden sm:inline">Licitațiile mele</span>
                        <span className="sm:hidden">Ale mele</span>
                        {myAuctions.length > 0 && (
                            <Badge variant="secondary" className="ml-1">{myAuctions.length}</Badge>
                        )}
                    </TabsTrigger>
                    <TabsTrigger value="participating" className="flex items-center gap-2" data-testid="tab-participating">
                        <History className="w-4 h-4" />
                        <span className="hidden sm:inline">Istoric licitări</span>
                        <span className="sm:hidden">Istoric</span>
                        {participatingAuctions.length > 0 && (
                            <Badge variant="secondary" className="ml-1">{participatingAuctions.length}</Badge>
                        )}
                    </TabsTrigger>
                </TabsList>

                {/* My Auctions Tab */}
                <TabsContent value="my-auctions">
                    <Card className="rounded-2xl">
                        <CardHeader className="pb-4">
                            <div className="flex items-center justify-between flex-wrap gap-4">
                                <div>
                                    <CardTitle className="flex items-center gap-2">
                                        <Gavel className="w-5 h-5 text-primary" />
                                        Licitațiile mele
                                    </CardTitle>
                                    <CardDescription>
                                        Licitații create de tine - vezi cine a licitat
                                    </CardDescription>
                                </div>
                                <Button onClick={() => navigate('/create-listing')} className="rounded-full" data-testid="create-auction-btn">
                                    <Plus className="w-4 h-4 mr-2" />
                                    Creează licitație
                                </Button>
                            </div>
                            {myAuctions.length > 3 && (
                                <div className="relative mt-4">
                                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                                    <Input
                                        placeholder="Caută în licitațiile tale..."
                                        value={searchQuery}
                                        onChange={(e) => setSearchQuery(e.target.value)}
                                        className="pl-10"
                                        data-testid="search-my-auctions"
                                    />
                                </div>
                            )}
                        </CardHeader>
                        <CardContent>
                            {filterAuctions(myAuctions).length === 0 ? (
                                <div className="text-center py-12 text-muted-foreground">
                                    <Gavel className="w-12 h-12 mx-auto mb-4 opacity-30" />
                                    <p>{searchQuery ? 'Nicio licitație găsită' : 'Nu ai nicio licitație'}</p>
                                    {!searchQuery && (
                                        <Button 
                                            onClick={() => navigate('/create-listing')} 
                                            className="mt-4 rounded-full"
                                            variant="outline"
                                        >
                                            Creează prima ta licitație
                                        </Button>
                                    )}
                                </div>
                            ) : (
                                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                                    {filterAuctions(myAuctions).map((auction) => (
                                        <MyAuctionCard key={auction.listing_id} auction={auction} />
                                    ))}
                                </div>
                            )}
                        </CardContent>
                    </Card>
                </TabsContent>

                {/* Participating Tab (Bidding History) */}
                <TabsContent value="participating">
                    <Card className="rounded-2xl">
                        <CardHeader className="pb-4">
                            <CardTitle className="flex items-center gap-2">
                                <History className="w-5 h-5 text-primary" />
                                Istoric licitări
                            </CardTitle>
                            <CardDescription>
                                {participatingAuctions.length} licitații la care ai plasat oferte
                            </CardDescription>
                        </CardHeader>
                        <CardContent>
                            {participatingAuctions.length === 0 ? (
                                <div className="text-center py-12 text-muted-foreground">
                                    <Gavel className="w-12 h-12 mx-auto mb-4 opacity-30" />
                                    <p>Nu ai participat la nicio licitație</p>
                                    <Button 
                                        onClick={() => navigate('/all-listings?listing_type=auction')} 
                                        className="mt-4 rounded-full"
                                        variant="outline"
                                    >
                                        Explorează licitații
                                    </Button>
                                </div>
                            ) : (
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    {participatingAuctions.map((auction) => (
                                        <ParticipatingAuctionCard key={auction.listing_id} auction={auction} />
                                    ))}
                                </div>
                            )}
                        </CardContent>
                    </Card>
                </TabsContent>
            </Tabs>
        </div>
    );
};

export default DashboardAuctionsTab;
